import { CalendarClock, RefreshCw } from 'lucide-react'

import type { TreatmentPrescriptionContext, TreatmentWithRelations } from '@snootsvet/shared'
import { formatRefillInterval, getPrimaryOrder } from '@snootsvet/shared'

import { FormattedDate } from '@/frontend/components/formatted-datetime'
import { Badge } from '@/frontend/components/ui/badge'

export function RefillIntervalBadge({
  rx,
}: {
  rx: Pick<TreatmentWithRelations, 'refillInterval' | 'isExternal'>
}) {
  if (rx.isExternal || !rx.refillInterval) return null

  return (
    <Badge variant="outline" size="xs" className="bg-muted/60 font-normal">
      <CalendarClock />
      Every {formatRefillInterval(rx.refillInterval)}
    </Badge>
  )
}

export function RefillStatusBadge({
  rx,
}: {
  rx: TreatmentWithRelations & TreatmentPrescriptionContext
}) {
  if (rx.isExternal || !rx.isActivePrescription) return null

  if (rx.isEligibleForRefill) {
    return (
      <Badge variant="success" size="xs">
        Available now
      </Badge>
    )
  }
  if (rx.refillsUsed >= (rx.repeatsAllowed ?? 0)) {
    return (
      <Badge variant="pending" size="xs">
        All refills used
      </Badge>
    )
  }
  if (rx.hasActiveOrder) {
    return (
      <Badge variant="pending" size="xs">
        Order in progress
      </Badge>
    )
  }
  if (rx.refillCooldownEndsAt && new Date(rx.refillCooldownEndsAt) > new Date()) {
    return (
      <Badge variant="pending" size="xs">
        Available from{' '}
        <FormattedDate
          value={rx.refillCooldownEndsAt}
          clinicId={getPrimaryOrder(rx.orders)?.clinicId ?? null}
        />
      </Badge>
    )
  }
  return (
    <Badge variant="warning" size="xs">
      Not currently eligible
    </Badge>
  )
}

export function RefillBadge({
  rx,
  size = 'xs',
}: {
  rx: TreatmentWithRelations & TreatmentPrescriptionContext
  size?: React.ComponentPropsWithoutRef<typeof Badge>['size']
}) {
  if (rx.isExternal || !rx.repeatsAllowed) return null
  const remaining = Math.max(0, rx.repeatsAllowed - rx.refillsUsed)

  return (
    <Badge variant="outline" size={size} className="bg-muted/60 font-normal">
      <RefreshCw />
      {remaining} of {rx.repeatsAllowed} {rx.repeatsAllowed === 1 ? 'refill' : 'refills'} remaining
    </Badge>
  )
}
